import { Address } from '@app/domain/Address'
import { PaymentsAccount, PaymentProviderCapability } from '@app/domain/PaymentsAccount'
import { Venue } from '@app/domain/Venue'
import { BaseUser } from './BaseUser'

export class Business {

    constructor(
        public id: string,
        public displayName: string,
        public phoneNumber: string,
        public address: Address,
        public venues: Venue[],
        public paymentsAccounts: PaymentsAccount[],
        public paymentsAccountAssignments?: Partial<Record<PaymentProviderCapability, string>>
    ) { }

    get paymentsAccount(): PaymentsAccount | null {
        const assignedIds = Object.values(this.paymentsAccountAssignments ?? {})
        return this.paymentsAccounts.find(account => assignedIds.includes(account.id) && account.isConnected)
            ?? this.paymentsAccounts.find(account => account.active && account.isConnected) ?? null
    }

    paymentsAccountForCapability(capability: PaymentProviderCapability): PaymentsAccount | null {
        const accountId = this.paymentsAccountAssignments?.[capability]
        const account = this.paymentsAccountAssignments === undefined
            ? this.paymentsAccount
            : this.paymentsAccounts.find(account => account.id === accountId && account.isConnected)
        return account?.supports(capability) ? account : null
    }

    get connectedPaymentsAccounts(): PaymentsAccount[] {
        return this.paymentsAccounts.filter(account => account.isConnected)
    }

    paymentsAccountsForCapability(capability: PaymentProviderCapability): PaymentsAccount[] {
        return this.connectedPaymentsAccounts.filter(account => account.supports(capability))
    }

    canAssignPaymentsAccount(capability: PaymentProviderCapability, account: PaymentsAccount): boolean {
        if (!account.isConnected || !account.supports(capability)
            || !this.paymentsAccounts.some(existing => existing.id === account.id)) {
            return false
        }
        return Object.entries(this.paymentsAccountAssignments ?? {}).every(([assignedCapability, accountId]) => {
            const assigned = this.paymentsAccounts.find(existing => existing.id === accountId)
            return assignedCapability === capability || !assigned || assigned.currencyCode === account.currencyCode
        })
    }

    hasAssignablePaymentsAccount(capability: PaymentProviderCapability): boolean {
        return this.paymentsAccountsForCapability(capability).some(account => this.canAssignPaymentsAccount(capability, account))
    }

    isPaymentsAccountAssigned(account: PaymentsAccount): boolean {
        return account.capabilities.some(capability => this.paymentsAccountForCapability(capability)?.id === account.id)
    }

    setPaymentsAccount(account: PaymentsAccount) {
        const existingIndex = this.paymentsAccounts.findIndex(existing => existing.id === account.id)
        if (existingIndex === -1) {
            if (this.paymentsAccounts.length === 0) {
                this.paymentsAccountAssignments = Object.fromEntries(account.capabilities.map(capability => [capability, account.id]))
            }
            this.paymentsAccounts.push(account)
        } else {
            this.paymentsAccounts.splice(existingIndex, 1, account)
        }
    }

    disconnectPaymentsAccount(account: PaymentsAccount) {
        this.paymentsAccounts = this.paymentsAccounts.map(existing => {
            if (existing.id === account.id) {
                return new PaymentsAccount(
                    existing.id,
                    existing.currencyCode,
                    existing.depositApplicationFeePercent,
                    existing.cancellationChargeApplicationFeePercent,
                    existing.voucherApplicationFeePercent,
                    existing.processingFeePercent,
                    existing.processingFeeUnitAmount,
                    existing.gateway,
                    existing.apiKey,
                    existing.applePayWebDomains,
                    existing.applePayEnabled,
                    false,
                    new Date(),
                    existing.capabilities,
                    existing.orderApplicationFeePercent
                )
            }
            return existing
        })
    }

    connectedPaymentsAccountForGateway(gateway: PaymentsAccount['gateway']): PaymentsAccount | null {
        return this.paymentsAccounts.find(account => account.gateway === gateway && account.isConnected) ?? null
    }

    addVenue(venue: Venue) {
        this.venues.push(venue)
    }

    getVenue(venueId: string): Venue | null {
        return this.venues.find(venue => venue.id === venueId) ?? null
    }

    updateVenue(venue: Venue) {
        const index = this.venues.findIndex(existingVenue => existingVenue.id === venue.id)
        if (index === -1) {
            return
        }
        this.venues.splice(index, 1, venue)
    }

    removeVenue(venue: Venue) {
        const index = this.venues.findIndex(existingVenue => existingVenue.id === venue.id)
        if (index === -1) {
            return
        }
        this.venues.splice(index, 1)
    }

    venuesUserWorksIn(user: BaseUser): Venue[] {
        return this.venues.filter(venue => user.worksInVenue(venue))
    }
}
